import { TrashIcon } from "@phosphor-icons/react";
import { formatPrice } from "apps/visits/core/utils/format-price";
import { number } from "mathjs";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/shared/v2/lib/utils";
import type { CartProduct } from "../../../models/cart-product";
import type { OrderItem } from "../../../models/order-item";
import { BloodTestsCount } from "../../blood-test-package/blood-tests-count";
import { ProductName } from "../product-name";

export type ProductsListProps = {
  products: (CartProduct | OrderItem)[];
  onRemoveProduct?: (product: CartProduct) => void;
  isNew?: (product: CartProduct | OrderItem) => boolean;
};

const rowKey = (product: CartProduct | OrderItem) => ("uuid" in product ? product.uuid : product.productUuid);

const rowPrice = (product: CartProduct | OrderItem) =>
  "quantity" in product ? number(product.grossPrice) * product.quantity : number(product.grossPrice);

const rowBloodTests = (product: CartProduct | OrderItem) =>
  "bloodTests" in product && product.bloodTests?.length ? product.bloodTests : undefined;

export const ProductsList = ({ products, onRemoveProduct, isNew }: ProductsListProps) => {
  const { t } = useTranslation();

  if (products.length === 0) return null;

  return (
    <ul className="mt-4">
      {products.map((product) => {
        const bloodTests = rowBloodTests(product);

        return (
          <li
            className={cn("flex items-start justify-between gap-3 py-2", {
              "slide-in-from-right-4 fade-in animate-in duration-300": isNew?.(product),
            })}
            key={rowKey(product)}
          >
            <div className="flex flex-col gap-0.5">
              <ProductName product={product} className="font-semibold text-foreground" />
              {bloodTests && <BloodTestsCount bloodTests={bloodTests} className="text-foreground" />}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[16px]">{formatPrice(rowPrice(product))}</span>
              {/* Only upsell add-ons are removable; the standard package is the base of the order. */}
              {onRemoveProduct && "isUpsell" in product && product.isUpsell && (
                <button
                  type="button"
                  className="cursor-pointer border-0 bg-transparent p-0 text-grey-700"
                  onClick={() => onRemoveProduct(product)}
                  aria-label={t("GROUP_ACTIONS_ORDERING.UPSELL.REMOVE_PACKAGE")}
                >
                  <TrashIcon style={{ width: "20px", height: "20px" }} />
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};
